import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Award, CalendarPlus, Camera, Clock, Copy, HandHeart, Loader2, LogOut, Share2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiError } from "@/lib/api";
import { volApi, VOLUNTEER_TOKEN_KEY } from "@/lib/volunteerApi";
import { shortDate } from "@/lib/format";

const SCOPES = [
  ["website", "Our website and the Wall of Community Heroes"],
  ["print", "Printed material — newsletters, annual report"],
  ["social", "Our social media accounts"],
];

const Tile = ({ label, value, sub, testId }) => (
  <div className="rounded-[14px] border border-border bg-card p-4" data-testid={testId}>
    <div className="text-[10.5px] uppercase tracking-[1.4px] text-muted-foreground">{label}</div>
    <div className="mt-1 font-display text-2xl font-bold text-foreground">{value}</div>
    {sub && <div className="mt-0.5 text-[11.5px] text-muted-foreground">{sub}</div>}
  </div>
);

export default function VolunteerPortal() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [impact, setImpact] = useState(null);
  const [shifts, setShifts] = useState(null);
  const [profile, setProfile] = useState(null);
  const [consent, setConsent] = useState({ granted: false, scopes: [] });
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [mine, imp, sh] = await Promise.all([
      volApi.get("/volunteer/me"),
      volApi.get("/volunteer/impact"),
      volApi.get("/volunteer/shifts"),
    ]);
    setMe(mine.data);
    setImpact(imp.data);
    setShifts(sh.data);
    const p = mine.data.profile || {};
    setProfile({
      bio: p.bio || "", skills: (p.skills || []).join(", "),
      languages: (p.languages || []).join(", "), availability: p.availability || "",
      phone: p.phone || "", occupation: p.occupation || "",
      emergency_contact_name: p.emergency_contact_name || "",
      emergency_contact_phone: p.emergency_contact_phone || "",
    });
    setConsent({
      granted: Boolean(mine.data.consent?.granted),
      scopes: mine.data.consent?.scopes || [],
    });
  }, []);

  useEffect(() => {
    load().catch((e) => {
      if (e?.response?.status === 401) navigate("/volunteer/signin", { replace: true });
      else toast.error(apiError(e));
    });
  }, [load, navigate]);

  const signOut = async () => {
    await volApi.post("/volunteer/logout").catch(() => {});
    localStorage.removeItem(VOLUNTEER_TOKEN_KEY);
    navigate("/volunteer/signin", { replace: true });
  };

  const act = async (key, fn, message) => {
    setBusy(key);
    try {
      await fn();
      toast.success(message);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const saveProfile = () => act("profile", () => volApi.put("/volunteer/me", {
    ...profile,
    skills: profile.skills.split(",").map((s) => s.trim()).filter(Boolean),
    languages: profile.languages.split(",").map((s) => s.trim()).filter(Boolean),
  }), "Your details are saved");

  const saveConsent = () => act("consent", () => volApi.post("/volunteer/consent", {
    granted: consent.granted && consent.scopes.length > 0,
    scopes: consent.granted ? consent.scopes : [],
  }), consent.granted && consent.scopes.length > 0
    ? "Permission recorded" : "Permission withdrawn");

  const claim = (ref) => act(`claim-${ref}`,
    () => volApi.post(`/volunteer/shifts/${ref}/claim`), "Your place is booked");

  const uploadPhoto = async (file) => {
    if (!file) return;
    setBusy("photo");
    try {
      const body = new FormData();
      body.append("file", file);
      await volApi.post("/volunteer/photo", body,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photograph saved");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(impact.share.line);
      toast.success("Copied — paste it wherever you like");
    } catch { toast.error("Copy is blocked in this browser"); }
  };

  if (!me || !impact || !shifts || !profile) {
    return <div className="grid min-h-screen place-items-center bg-background">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/70 px-5 py-4 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-[10px] bg-gradient-to-br from-primary to-[hsl(var(--teal))]">
              <HandHeart className="h-4 w-4 text-white" />
            </span>
            <div>
              <div className="text-[10px] uppercase tracking-[1.6px] text-muted-foreground">
                Volunteer account
              </div>
              <div className="font-display text-[16px] font-bold text-foreground"
                   data-testid="portal-name">{me.name}</div>
            </div>
          </div>
          <Button variant="outline" onClick={signOut} data-testid="portal-signout"
                  className="rounded-full text-[12px]">
            <LogOut className="mr-1.5 h-3.5 w-3.5" /> Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-5 px-5 py-7">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Tile testId="portal-hours" label="Approved hours" value={impact.hours}
                sub={`${impact.entries} entries`} />
          <Tile testId="portal-milestone" label="Milestone"
                value={impact.milestone.badge ? `${impact.milestone.badge}h` : "—"}
                sub={impact.milestone.next
                  ? `${impact.milestone.to_next}h to ${impact.milestone.next}h` : "Every hour counts"} />
          <Tile testId="portal-my-shifts" label="My shifts" value={shifts.mine.length}
                sub={`${shifts.open.length} places open`} />
          <Tile testId="portal-programmes" label="Programmes" value={impact.programmes.length}
                sub={impact.programmes.slice(0, 2).join(", ") || "General help"} />
        </div>

        <Tabs defaultValue="shifts">
          <TabsList data-testid="portal-tabs">
            <TabsTrigger value="shifts" data-testid="tab-portal-shifts">My rota</TabsTrigger>
            <TabsTrigger value="hours" data-testid="tab-portal-hours">My hours</TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-portal-profile">My details</TabsTrigger>
            <TabsTrigger value="consent" data-testid="tab-portal-consent">
              Photos &amp; story
            </TabsTrigger>
          </TabsList>

          <TabsContent value="shifts" className="mt-4 space-y-4">
            <section className="rounded-[14px] border border-border bg-card p-5">
              <h2 className="text-base font-semibold text-foreground md:text-lg">My shifts</h2>
              {shifts.mine.length === 0 ? (
                <p className="mt-2 text-[12.5px] text-muted-foreground" data-testid="portal-mine-empty">
                  You have no shifts booked.
                </p>
              ) : (
                <ul className="mt-3 space-y-2" data-testid="portal-mine-list">
                  {shifts.mine.map((s) => (
                    <li key={s.shift_ref}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-muted/20 px-3 py-2">
                      <div>
                        <div className="text-[13px] font-medium text-foreground">{s.title}</div>
                        <div className="text-[11.5px] text-muted-foreground">
                          {shortDate(s.date)} · {s.starts}–{s.ends} · {s.hours}h
                        </div>
                      </div>
                      <span className="rounded-full border border-border px-2 py-0.5 font-mono text-[10px] capitalize text-muted-foreground">
                        {s.status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="rounded-[14px] border border-border bg-card p-5">
              <h2 className="text-base font-semibold text-foreground md:text-lg">Places open</h2>
              {shifts.open.length === 0 ? (
                <p className="mt-2 text-[12.5px] text-muted-foreground" data-testid="portal-open-empty">
                  Nothing is open at the moment — do check back.
                </p>
              ) : (
                <ul className="mt-3 space-y-2" data-testid="portal-open-list">
                  {shifts.open.map((s) => (
                    <li key={s.shift_ref}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border px-3 py-2">
                      <div>
                        <div className="text-[13px] font-medium text-foreground">{s.title}</div>
                        <div className="text-[11.5px] text-muted-foreground">
                          {shortDate(s.date)} · {s.starts}–{s.ends} · {s.places_left} of {s.places} free
                          {s.skills?.length ? ` · ${s.skills.join(", ")}` : ""}
                        </div>
                      </div>
                      <Button size="sm" data-testid={`claim-${s.shift_ref}`}
                              disabled={busy === `claim-${s.shift_ref}`}
                              onClick={() => claim(s.shift_ref)}
                              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                        <CalendarPlus className="mr-1.5 h-3.5 w-3.5" /> Take this shift
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </TabsContent>

          <TabsContent value="hours" className="mt-4 space-y-4">
            <section className="rounded-[14px] border border-border bg-card p-5">
              <h2 className="flex items-center gap-2 text-base font-semibold text-foreground md:text-lg">
                <Clock className="h-4 w-4 text-primary" /> Recently approved
              </h2>
              {impact.recent.length === 0 ? (
                <p className="mt-2 text-[12.5px] text-muted-foreground" data-testid="portal-hours-empty">
                  No approved hours yet. Hours appear here once your coordinator approves them.
                </p>
              ) : (
                <ul className="mt-3 divide-y divide-border" data-testid="portal-hours-list">
                  {impact.recent.map((e, i) => (
                    <li key={i} className="flex items-center justify-between py-2">
                      <span className="text-[12.5px] text-foreground">
                        {e.against || "General help"}
                      </span>
                      <span className="font-mono text-[11.5px] text-muted-foreground">
                        {shortDate(e.date)} · {e.hours}h
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="rounded-[14px] border border-border bg-card p-5"
                     data-testid="portal-share-card">
              <h2 className="flex items-center gap-2 text-base font-semibold text-foreground md:text-lg">
                <Award className="h-4 w-4 text-primary" /> {impact.share.headline}
              </h2>
              <p className="mt-2 text-[13px] text-muted-foreground">{impact.share.line}</p>
              {impact.shareable ? (
                <Button onClick={copyShare} data-testid="portal-share-copy"
                        className="mt-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  <Share2 className="mr-1.5 h-3.5 w-3.5" /> Copy my share message
                </Button>
              ) : (
                <p className="mt-3 text-[11.5px] text-amber-600 dark:text-amber-400">
                  Give permission for social media under <strong>Photos &amp; story</strong> to share
                  this.
                </p>
              )}
            </section>
          </TabsContent>

          <TabsContent value="profile" className="mt-4">
            <section className="space-y-4 rounded-[14px] border border-border bg-card p-5">
              <h2 className="text-base font-semibold text-foreground md:text-lg">My details</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {[["phone", "Telephone"], ["occupation", "Occupation"],
                  ["availability", "When I am free"], ["skills", "Skills (comma separated)"],
                  ["languages", "Languages (comma separated)"],
                  ["emergency_contact_name", "Emergency contact name"],
                  ["emergency_contact_phone", "Emergency contact telephone"]].map(([k, label]) => (
                  <div key={k} className="space-y-1.5">
                    <Label className="text-[11.5px] text-muted-foreground">{label}</Label>
                    <Input value={profile[k]} data-testid={`portal-${k}`}
                           onChange={(e) => setProfile({ ...profile, [k]: e.target.value })} />
                  </div>
                ))}
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11.5px] text-muted-foreground">About me</Label>
                <Textarea rows={3} value={profile.bio} data-testid="portal-bio"
                          onChange={(e) => setProfile({ ...profile, bio: e.target.value })} />
              </div>
              <Button onClick={saveProfile} disabled={busy === "profile"} data-testid="portal-save-profile"
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                Save my details
              </Button>
            </section>
          </TabsContent>

          <TabsContent value="consent" className="mt-4">
            <section className="space-y-3 rounded-[14px] border border-border bg-card p-5">
              <h2 className="text-base font-semibold text-foreground md:text-lg">
                Photographs and my story
              </h2>
              <p className="text-[12.5px] text-muted-foreground">
                You decide where we may use your name, photograph and story, and you can withdraw it
                here at any time. Withdrawing removes you from the public wall straight away.
              </p>
              <div className="flex items-center gap-4">
                {impact.has_photo
                  ? <img alt="" data-testid="portal-photo"
                         src={`${BACKEND_URL}${impact.photo_url || impact.photo_self_url}`}
                         className="h-20 w-20 rounded-xl object-cover" />
                  : <span className="grid h-20 w-20 place-items-center rounded-xl bg-muted text-[11px] text-muted-foreground"
                          data-testid="portal-photo-empty">
                      No photo
                    </span>}
                <div className="space-y-1.5">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border px-3.5 py-2 text-[12px] text-muted-foreground transition-colors duration-150 hover:text-foreground">
                    <input type="file" accept="image/*" className="hidden"
                           data-testid="portal-photo-input"
                           onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                    {busy === "photo" ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Camera className="h-3.5 w-3.5" />}
                    {impact.has_photo ? "Replace my photograph" : "Upload my photograph"}
                  </label>
                  {impact.has_photo && (
                    <Button size="sm" variant="outline" data-testid="portal-photo-remove"
                            className="rounded-full text-[11px]" disabled={busy === "photo-del"}
                            onClick={() => act("photo-del", () => volApi.delete("/volunteer/photo"),
                              "Photograph removed")}>
                      Remove it
                    </Button>
                  )}
                  {impact.has_photo && !impact.photo_url && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400">
                      Your photograph is on file but not published — website consent is not held.
                    </p>
                  )}
                </div>
              </div>
              <label className="flex items-start gap-2.5 rounded-xl border border-border bg-muted/25 p-3">
                <Checkbox checked={consent.granted} data-testid="portal-consent-granted"
                          onCheckedChange={(v) => setConsent({ ...consent, granted: Boolean(v) })} />
                <span className="text-[12.5px] text-foreground">
                  I give permission for my name, photograph and story to be used.
                </span>
              </label>
              {consent.granted && (
                <div className="space-y-2 pl-1" data-testid="portal-consent-scopes">
                  {SCOPES.map(([scope, label]) => (
                    <label key={scope} className="flex items-start gap-2.5">
                      <Checkbox checked={consent.scopes.includes(scope)}
                                data-testid={`portal-consent-${scope}`}
                                onCheckedChange={() => setConsent({
                                  ...consent,
                                  scopes: consent.scopes.includes(scope)
                                    ? consent.scopes.filter((s) => s !== scope)
                                    : [...consent.scopes, scope],
                                })} />
                      <span className="text-[12.5px] text-muted-foreground">{label}</span>
                    </label>
                  ))}
                </div>
              )}
              <Button onClick={saveConsent} data-testid="portal-save-consent"
                      disabled={busy === "consent"}
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {consent.granted && consent.scopes.length > 0
                  ? "Save my permission" : "Withdraw my permission"}
              </Button>
              {consent.granted && consent.scopes.length === 0 && (
                <p className="text-[11.5px] text-amber-600 dark:text-amber-400"
                   data-testid="portal-consent-none">
                  With nothing ticked, saving withdraws your permission everywhere.
                </p>
              )}
              {impact.wall_live && consent.granted && consent.scopes.includes("website") && (
                <p className="text-[11.5px] text-muted-foreground" data-testid="portal-wall-live">
                  You are on the public Wall of Community Heroes.
                </p>
              )}
            </section>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
